/**
 * Tipi della vista "Batch Edits" (specchio delle risposte di product-service /batch-edits*).
 */

export type BatchStatus =
  | 'created'
  | 'uploading'
  | 'validated'
  | 'on-hold'
  | 'invalid'
  | 'queued'
  | 'running'
  | 'completed'
  | 'completed-with-errors'
  | 'failed';

export type RecordKind = 'new' | 'update' | 'delete' | 'unchanged';
export type RecordStatus =
  | 'pending'
  | 'done'
  | 'failed'
  | 'pending-dt'
  | 'skipped'
  | 'not-executed';
export type BatchEntity =
  | 'product'
  | 'release'
  | 'component'
  | 'mapping'
  | 'customer'
  | 'site'
  | 'contact'
  | 'deployment';

export type StatusCounts = Record<RecordStatus, number>;

export interface RecordStats {
  total: number;
  byStatus: StatusCounts;
  byKind: Record<RecordKind, StatusCounts>;
  byEntity: Partial<Record<BatchEntity, Record<RecordKind, number>>>;
}

export interface BatchError {
  code: string;
  message: string;
  path?: string;
  hint?: string;
  chunkIndex?: number;
  chunkPath?: string;
}

export interface PlanSummary {
  create: number;
  reactivate: number;
  update: number;
  delete: number;
  unchanged: number;
}

/**
 * Batch messo in attesa di conferma: N = create + reactivate + update + delete del piano era
 * ≥ della soglia (si esegue automaticamente solo con N < soglia).
 */
export interface BatchHold {
  changes: number;
  breakdown: { create: number; reactivate: number; update: number; delete: number };
  threshold: number;
  heldAt: string;
  forcedBy?: string;
  forcedAt?: string;
  forcedChanges?: number;
  forcedThreshold?: number;
}

/** Riga dell'elenco batch (GET /batch-edits). */
export interface BatchSummary {
  batchId: string;
  status: BatchStatus;
  description?: string;
  mode: 'full' | 'partial';
  prune: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  validatedAt?: string;
  queuedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  chunks: { uploaded: number; expected?: number };
  counts: { products: number; customers: number; deployments: number };
  planSummary?: PlanSummary;
  progress: { processed: number; total: number; percent: number };
  queuePosition?: number;
  errorCount: number;
  warningCount: number;
  stats?: RecordStats;
  /** Presente se il batch è (o è stato) in attesa di conferma per soglia superata. */
  hold?: BatchHold;
  /** Motivo dell'attesa (solo con status `on-hold`). */
  holdReason?: { it: string; en: string };
}

export interface BatchEvent {
  at: string;
  type: string;
  message: string;
  actor?: string;
  details?: Record<string, unknown>;
}

export interface BatchChunkInfo {
  chunkIndex: number;
  uploadedAt: string;
  counts: { products: number; customers: number; deployments: number };
}

/** Dettaglio (GET /batch-edits/:id). */
export interface BatchDetail extends BatchSummary {
  errors: BatchError[];
  warnings: Array<{ path: string; code: string; message: string }>;
  events: BatchEvent[];
  chunkList: BatchChunkInfo[];
}

export interface BatchRecord {
  seq: number;
  entity: BatchEntity;
  key: string;
  op: 'create' | 'reactivate' | 'update' | 'delete' | 'unchanged';
  kind: RecordKind;
  status: RecordStatus;
  changedFields?: string[];
  error?: string;
  chunkIndex?: number;
}

export interface RecordsPage {
  items: BatchRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RecordsQuery {
  kind?: RecordKind;
  status?: RecordStatus;
  entity?: BatchEntity;
  q?: string;
  page: number;
  pageSize: number;
}

/** Stati in cui il batch non cambierà più (polling disattivabile). */
export const FINISHED_STATUSES: readonly BatchStatus[] = [
  'completed',
  'completed-with-errors',
  'failed',
];
/** Stati in cui il batch sta per essere / viene eseguito. */
export const ACTIVE_STATUSES: readonly BatchStatus[] = ['queued', 'running'];

/** Risposta di POST /batch-edits/:id/force-run (202). */
export interface ForceRunResponse {
  status: 'started' | 'waiting-for-others-to-complete';
  batchId: string;
  queuePosition?: number;
  forced: true;
  changes: number;
  threshold: number;
  forcedBy: string;
}
